import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Dashboard } from "@/features/patient/dashboard/components/DashboardView";
import { Button } from "@/shared/components/button";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { usePatientData } from "@/portals/patient/context/PatientDataContext";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { AccessRequestsBanner } from "@/features/patient/family/components/AccessRequestsBanner";
import { ClaimGuidanceCard } from "@/features/patient/family/components/ClaimGuidanceCard";
import { getPendingClaimGuidance, clearPendingClaimGuidance } from "@/portals/patient/pages/auth/claimGuidance";
import { hasPendingSsnPrompt, clearPendingSsnPrompt } from "@/portals/patient/pages/auth/ssnPrompt";
import { SsnModal } from "@/portals/patient/pages/profile/SsnModal";
import { usePatientProfile } from "@/portals/patient/context/PatientProfileContext";
import { samplePatientConcerns } from "@/data/patient/records.mock";
import { sampleHealthConcerns } from "@/data/patient/extended.mock";
import { useEpicConnectReturn } from "@/features/patient/epic-connect/hooks/useEpicConnectReturn";

function DependentDashboardBanner({ name, onSwitchBack }: { name: string; onSwitchBack: () => void }) {
  const initials = name.split(" ").filter(Boolean).slice(0, 2).map((part) => part.charAt(0)).join("").toUpperCase();

  return (
    <div className="mx-4 mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-[#180047] px-5 py-4 lg:mx-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#9187A8] text-xs font-bold !text-white">
          {initials || "?"}
        </div>
        <div>
          <h2 className="text-sm font-extrabold !text-white">{name}'s Records</h2>
          <p className="mt-0.5 text-xs text-[#C7C2D6]">You're viewing {name}'s health records and medical information.</p>
        </div>
      </div>
      <Button
        onClick={onSwitchBack}
        className="msq-switch-dashboard-button !flex !items-center !gap-1.5 !rounded-lg !border-none !px-4 !py-2 !text-xs !font-semibold !text-white"
      >
        Switch back to my dashboard
        <Icon icon="lucide:arrow-right" className="h-3.5 w-3.5 !text-white" />
      </Button>
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { records, setSelectedRecord } = usePatientData();
  const { zipUploads, isLoading: isUploadHistoryLoading } = useUploadHistory();
  const { activeDependent, setActiveDependentId, incomingPatientRequests, refresh: refreshFamily } = useFamily();
  const currentEmail = usePatientAuthStore((s) => s.user?.email);
  // Second (and last) showing of the claim guidance card — the first was
  // right after OTP verification (see VerifyOtpPage). Read once on mount,
  // not on every render, so dismissing it can't be raced by an unrelated
  // re-render re-reading a flag that's about to be cleared anyway.
  const [pendingGuidance, setPendingGuidance] = useState(() =>
    currentEmail ? getPendingClaimGuidance(currentEmail) : null
  );
  const dismissGuidance = () => {
    clearPendingClaimGuidance();
    setPendingGuidance(null);
  };
  // One-time optional SSN step for a brand-new signup (see ssnPrompt.ts).
  // Waits until the claim guidance card (if any) is dismissed, so the two
  // never stack. Skipping or saving both clear it for good; it stays
  // available from the Profile page.
  const { setProfile, profile } = usePatientProfile();
  const [ssnPromptPending, setSsnPromptPending] = useState(() =>
    currentEmail ? hasPendingSsnPrompt(currentEmail) : false
  );
  const closeSsnPrompt = () => {
    clearPendingSsnPrompt();
    setSsnPromptPending(false);
  };
  // Independent of `records` (which the real Epic/Athena upload path never
  // populates — see UploadRecords.tsx's beginZipUpload) — this reflects whether
  // any real upload has actually finished processing, so a patient with real
  // parsed data doesn't get stuck on the empty state forever.
  const hasRealData = zipUploads.some((z) => z.status === "complete");
  // Backend redirects Epic connections back to /dashboard?epic_connected=1 (or
  // ?epic_error=<CODE>) — see the /dashboard route alias in router.tsx.
  useEpicConnectReturn();

  const routeMap: Record<string, string> = {
    dashboard: "/patient",
    upload: "/patient/upload",
    timeline: "/patient/timeline",
    ask: "/patient/ask",
    diagnosis: "/patient/diagnosis",
    "health-concerns": "/patient/health-concerns",
    "complete-history": "/patient/complete-history",
    medications: "/patient/medications",
    diagnostics: "/patient/diagnostics",
    "care-treatment": "/patient/care-treatment",
    "insurance-cards": "/patient/insurance-cards",
    claims: "/patient/claims",
    "fhir-export": "/patient/fhir-export",
    "human-review-queue": "/patient/human-review-queue",
    "patient-only-queue": "/patient/patient-only-queue",
  };

  if (activeDependent) {
    return (
      <div className="min-h-full bg-[#F9F9F9]">
        <DependentDashboardBanner name={activeDependent.name} onSwitchBack={() => setActiveDependentId(null)} />
        <AccessRequestsBanner requests={incomingPatientRequests} onChange={refreshFamily} />
        <Dashboard
          records={records}
          healthConcerns={sampleHealthConcerns}
          patientConcerns={samplePatientConcerns}
          onRecordSelect={(record) => {
            setSelectedRecord(record);
            navigate(`/patient/records/${record.id}`);
          }}
          onNavigate={(view) => navigate(routeMap[view] ?? "/patient")}
          hasRealData={hasRealData}
          isCheckingUploads={isUploadHistoryLoading}
        />
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <AccessRequestsBanner requests={incomingPatientRequests} onChange={refreshFamily} />
      <Dashboard
        records={records}
        healthConcerns={sampleHealthConcerns}
        patientConcerns={samplePatientConcerns}
        onRecordSelect={(record) => {
          setSelectedRecord(record);
          navigate(`/patient/records/${record.id}`);
        }}
        onNavigate={(view) => navigate(routeMap[view] ?? "/patient")}
        hasRealData={hasRealData}
        isCheckingUploads={isUploadHistoryLoading}
      />
      {pendingGuidance && (
        <ClaimGuidanceCard
          creatorName={pendingGuidance.creatorName}
          onDismiss={dismissGuidance}
          onGoToCaregivers={() => { dismissGuidance(); navigate("/patient/manage-caregivers"); }}
        />
      )}
      <SsnModal
        open={ssnPromptPending && !pendingGuidance && !profile.ssnProvidedAt}
        onClose={closeSsnPrompt}
        onSaved={(ssnProvidedAt) => setProfile({ ...profile, ssnProvidedAt })}
      />
    </div>
  );
}
